"use client";

import { AgentThreadVo } from "@/api";
import { Loading } from "@/component/Loading";
import {
  CommunityThreadConfig,
  CommunityThreadItem,
} from "@/component/ThreadListItem/CommunityThreadItem";
import { fetchCommunityThreads } from "@/service/communityThreads";
import { getThreadSharePath } from "@/util/share";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import useSWRInfinite from "swr/infinite";

const PAGE_SIZE = 20;

const getKey = (
  pageIndex: number,
  previousPageData: AgentThreadVo[] | null
) => {
  if (previousPageData && !previousPageData.length) return null;
  return { offset: pageIndex * PAGE_SIZE, limit: PAGE_SIZE, type: "community" };
};

export function CommunityList() {
  const [isMuted, setIsMuted] = useState(true);
  const onToggleMuted = () => setIsMuted((prev) => !prev);
  const router = useRouter();
  const { data, setSize, isValidating, isLoading } = useSWRInfinite<
    AgentThreadVo[]
  >(getKey, (params) => fetchCommunityThreads(params), {
    revalidateFirstPage: false,
    persistSize: true,
  });

  const threads = useMemo(
    () =>
      data
        ? data.flat().map((thread) => {
            const options = thread.options as
              | {
                  aspect_ratio: string;
                  knowledgeId: string;
                }
              | undefined;
            const tags = thread.tags as
              | {
                  duration: number;
                  inputs: string[];
                }
              | undefined;

            const RATIO_LIST = ["16:9", "9:16"];

            const aspectRatio = RATIO_LIST.includes(options?.aspect_ratio || "")
              ? options?.aspect_ratio
              : "16:9";
            const knowledgeId = options?.knowledgeId || "";

            const duration = tags?.duration || 0;
            const inputs = tags?.inputs || [];

            return {
              id: thread.id || "",
              title: thread.title || "",
              imageUrl: thread.imageUrl || "",
              latestResultVideoUrl: thread.latestResultVideoUrl || "",
              options: {
                aspectRatio: aspectRatio as "16:9" | "9:16",
                knowledgeId,
              },
              tags: {
                duration,
                inputs,
              },
            } as CommunityThreadConfig;
          })
        : [],
    [data]
  );
  const hasMore = useMemo(() => {
    if (!data || data.length === 0) return true;
    return data[data.length - 1].length === PAGE_SIZE;
  }, [data]);

  const loaderRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);
  const hasMoreRef = useRef(true);

  useEffect(() => {
    isFetchingRef.current = isValidating || isLoading;
  }, [isValidating, isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  // Avoid hydration mismatch by ensuring initial column calculation is stable
  const [mounted, setMounted] = useState(false);
  const [columnCount, setColumnCount] = useState(4);

  useEffect(() => {
    setMounted(true);
    const updateColumns = () => {
      const width = window.innerWidth;
      if (width >= 1536) setColumnCount(5);
      else if (width >= 1280) setColumnCount(4);
      else if (width >= 768) setColumnCount(3);
      else if (width >= 640) setColumnCount(2);
      else setColumnCount(1);
    };

    updateColumns();
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  useEffect(() => {
    if (!mounted) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !isFetchingRef.current &&
          hasMoreRef.current
        ) {
          setSize((s) => s + 1);
        }
      },
      {
        rootMargin: "200px",
        threshold: 0,
      }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [setSize, mounted]);

  const columns = useMemo(() => {
    const cols: CommunityThreadConfig[][] = Array.from(
      { length: columnCount },
      () => []
    );
    // On server (or before mount), we just distribute items evenly to avoid skew
    if (!mounted) {
      threads.forEach((thread, idx) => {
        cols[idx % columnCount].push(thread);
      });
      return cols;
    }

    const colHeights = Array(columnCount).fill(0);

    threads.forEach((thread) => {
      const aspectRatioStr = thread.options.aspectRatio;
      const heightFactor = aspectRatioStr === "9:16" ? 16 / 9 : 9 / 16;

      let shortestIdx = 0;
      for (let i = 1; i < columnCount; i++) {
        if (colHeights[i] < colHeights[shortestIdx]) {
          shortestIdx = i;
        }
      }
      cols[shortestIdx].push(thread);
      colHeights[shortestIdx] += heightFactor;
    });

    return cols;
  }, [threads, columnCount, mounted]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-center items-start gap-0.5">
        {columns.map((column, colIdx) => (
          <div key={colIdx} className="flex flex-col flex-1 gap-0.5">
            {column.map((thread) => (
              <Link
                href={getThreadSharePath(thread.id || "")}
                key={thread.id}
                className="block p-0 border-0 bg-transparent cursor-pointer text-left"
                onMouseEnter={() => {
                  router.prefetch(getThreadSharePath(thread.id || ""));
                }}
                data-disable-progress={true}
              >
                <CommunityThreadItem
                  thread={thread}
                  isMuted={isMuted}
                  onToggleMuted={onToggleMuted}
                />
              </Link>
            ))}
          </div>
        ))}
      </div>

      <div ref={loaderRef} className="flex justify-center p-8 min-h-[100px]">
        {isValidating || isLoading ? (
          <Loading fontSize={16} color="var(--color-text-primary)" />
        ) : (
          <div className="h-1" /> // Spacer to keep observer triggerable
        )}
      </div>
    </div>
  );
}
